import {
  DBSchema,
  IDBPDatabase,
  openDB,
  OpenDBCallbacks,
  StoreKey,
  StoreNames,
  StoreValue,
} from "idb";

import {
  addBreadcrumb,
  captureException,
  captureMessage,
} from "@sentry/browser";

type Conn<DBTypes extends DBSchema | unknown = unknown> = {
  ctxId: number;
  db: IDBPDatabase<DBTypes>;
} | null;

class DbConnection<DBTypes extends DBSchema | unknown = unknown> {
  // allows to catch re-opens of the same DB across async calls
  private _ctxId: number = 1;
  private _db: Conn<DBTypes> = null;
  private _openingPromise: Promise<Conn<DBTypes>> | null = null;

  constructor(
    private readonly name: string,
    private readonly version: number,
    private readonly openCallbacks: OpenDBCallbacks<DBTypes>
  ) {
    this._openingPromise = this.open();
  }

  public async run<TRet>(
    callback: (db: IDBPDatabase<DBTypes>) => Promise<TRet>
  ): Promise<TRet> {
    let tries = 0;
    let error: Error | null = null;
    while (tries++ < 3) {
      let db: Conn<DBTypes> | null = this._db;
      try {
        if (!db) {
          this._openingPromise ||= this.open();
          db = await this._openingPromise;
        }

        // re-open called during async open
        // retrieve the new connection
        if (!db || db.ctxId !== this._ctxId) {
          continue;
        }
        return await callback(db.db);
      } catch (err) {
        // re-open called during async open
        // retrieve the new connection
        if (db?.ctxId !== this._ctxId) {
          continue;
        }
        error = err;
      }
    }
    throw error || new Error("Failed to run DB operation");
  }

  public async open(): Promise<Conn<DBTypes> | null> {
    const ctxId = this._ctxId;

    await idbReady(); // fix for Safari IndexedDB bug

    try {
      const db = await openDB<DBTypes>(
        this.name,
        this.version,
        this.openCallbacks
      );
      if (this._ctxId === ctxId) {
        this._openingPromise = null;
        return (this._db = { ctxId, db });
      }

      db.close();
      return null;
    } catch (err) {
      this._openingPromise = null;
      captureException(err, {
        extra: { dbName: this.name, dbVersion: this.version },
      });
      return null;
    }
  }

  public reOpen(): void {
    this._ctxId++;

    // close old connection
    this._db?.db.close();
    this._openingPromise?.then((db) => db?.db.close());
    this._db = null;
    this._openingPromise = null;
  }
}

export abstract class DB<DBTypes extends DBSchema | unknown = unknown>
  implements OpenDBCallbacks<DBTypes>
{
  private readonly conn: DbConnection<DBTypes>;

  constructor(
    public readonly name: string,
    public readonly version: number
  ) {
    this.conn = new DbConnection<DBTypes>(name, version, {
      upgrade: this.upgrade.bind(this),
      blocked: this.blocked.bind(this),
      blocking: this.blocking.bind(this),
      terminated: this.terminated.bind(this),
    });
  }

  abstract upgrade(
    db: IDBPDatabase<DBTypes>,
    oldVersion: number,
    newVersion: number
  );

  public get<Name extends StoreNames<DBTypes>>(
    storeName: Name,
    query: StoreKey<DBTypes, Name>
  ): Promise<StoreValue<DBTypes, Name> | undefined> {
    return this.conn.run((db) => db.get(storeName, query));
  }

  public getAll<Name extends StoreNames<DBTypes>>(
    storeName: Name,
    query?: StoreKey<DBTypes, Name> | IDBKeyRange | null,
    count?: number
  ): Promise<StoreValue<DBTypes, Name>[]> {
    return this.conn.run((db) => db.getAll(storeName, query, count));
  }

  public put<Name extends StoreNames<DBTypes>>(
    storeName: Name,
    value: StoreValue<DBTypes, Name>,
    key?: StoreKey<DBTypes, Name> | IDBKeyRange
  ): Promise<StoreKey<DBTypes, Name>> {
    return this.conn.run((db) => db.put(storeName, value, key));
  }

  blocked(
    currentVersion: number,
    blockedVersion: number | null,
    event: IDBVersionChangeEvent
  ): void {
    captureMessage(
      `DB blocked: ${this.name} v${blockedVersion} blocked by v${currentVersion}`,
      { level: "error" }
    );
  }

  blocking(
    currentVersion: number,
    blockedVersion: number | null,
    event: IDBVersionChangeEvent
  ): void {
    captureMessage(
      `DB blocking: ${this.name} v${currentVersion} is blocking new v${blockedVersion}`,
      { level: "error" }
    );
  }

  terminated(): void {
    this.conn.reOpen();

    addBreadcrumb({
      category: "database",
      message: `DB terminated: ${this.name} v${this.version}`,
      level: "info",
    });
  }
}

/**
 * Work around Safari 14 IndexedDB open bug.
 *
 * Safari has a horrible bug where IDB requests can hang while the browser is starting up. https://bugs.webkit.org/show_bug.cgi?id=226547
 * The only solution is to keep nudging it until it's awake.
 *
 * from: https://github.com/jakearchibald/safari-14-idb-fix/blob/2a9084feb17a1226a5aed0fd3f3d3a49c5b3e9eb/src/index.ts
 */
function idbReady(): Promise<void> {
  const isSafari =
    !navigator["userAgentData"] &&
    /Safari\//.test(navigator.userAgent) &&
    !/Chrom(e|ium)\//.test(navigator.userAgent);

  // No point putting other browsers or older versions of Safari through this mess.
  if (!isSafari || !indexedDB.databases) return Promise.resolve();

  let intervalId: ReturnType<typeof setInterval>;

  return new Promise<void>((resolve) => {
    const tryIdb = () =>
      indexedDB
        .databases()
        .catch((_) => null) // ensure no unhandled rejection
        .finally(resolve);
    intervalId = setInterval(tryIdb, 100);
    tryIdb();
  }).finally(() => clearInterval(intervalId));
}
